import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/** The Athlyn app's store package (the select's default option). */
export const ATHLYN_APP_PACKAGE = "com.athlyn.app";
/** The Amplify+ app's store package. */
export const AMPLIFY_APP_PACKAGE = "com.athlyn.amplifyplus";
/** The select value that reveals the free-text package field. */
export const OTHER_APP_PACKAGE = "other";

/** The select's known-app options (owner Q7): Athlyn (default), Amplify+, then "Other…". */
export const APP_PACKAGE_OPTIONS = [
  { value: ATHLYN_APP_PACKAGE, label: "Athlyn (com.athlyn.app)" },
  { value: AMPLIFY_APP_PACKAGE, label: "Amplify+ (com.athlyn.amplifyplus)" },
] as const;

const OPTION_PACKAGES: readonly string[] = APP_PACKAGE_OPTIONS.map((o) => o.value);

/** Same rule as the API: lower-case segments joined by dots, at least two segments. */
const PACKAGE_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;
const APP_STORE_ID_PATTERN = /^\d{6,12}$/;

export const APP_PACKAGE_HELP =
  "The app employees install. Also used for push notifications and app updates — change it only when the company moves to another app.";
export const APP_STORE_ID_HELP =
  "The number in the app's App Store link, e.g. 6758803089. Leave empty for Athlyn and Amplify+.";

export const APP_PACKAGE_REQUIRED_ERROR = "Enter the app's package, e.g. com.company.app";
export const APP_PACKAGE_FORMAT_ERROR = "Use a package like com.company.app — lower-case letters, digits and dots";
export const APP_STORE_ID_FORMAT_ERROR = "Use the 6–12 digit number from the app's App Store link";
export const APP_STORE_ID_REQUIRED_ERROR = "Add the App Store ID — this app isn't Athlyn or Amplify+";

/** The form state of the two app fields. */
export interface AppPackageValue {
  /** A select value: an option's package, OTHER_APP_PACKAGE, a stored known package, or "" (none set). */
  choice: string;
  /** The typed package when `choice` is "Other…". */
  other: string;
  appStoreId: string;
}

export type AppPackageField = "appScheme" | "appStoreId";

/** Trimmed and lower-cased, as the API stores it. */
export const normalizePackage = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase();

/** The package the form currently names (normalised), or null when none is set. */
export function selectedPackage(value: AppPackageValue): string | null {
  const pkg = value.choice === OTHER_APP_PACKAGE ? normalizePackage(value.other) : normalizePackage(value.choice);
  return pkg || null;
}

/**
 * The edit dialog's starting state from the saved row. A package that is one of the select's
 * options selects it; a known package that isn't (e.g. Dev's `dev.athlyn.app`) is shown as itself;
 * any other package opens "Other…" with it typed in.
 */
export function appPackageValueFrom(
  appScheme: string | null | undefined,
  appStoreId: string | null | undefined,
  isKnownApp: boolean,
): AppPackageValue {
  const pkg = normalizePackage(appScheme);
  const id = appStoreId ?? "";
  if (!pkg) return { choice: "", other: "", appStoreId: id };
  if (OPTION_PACKAGES.includes(pkg) || isKnownApp) return { choice: pkg, other: "", appStoreId: id };
  return { choice: OTHER_APP_PACKAGE, other: pkg, appStoreId: id };
}

/**
 * Field errors (empty when valid). The App Store ID is required only for "Other…" with a package
 * that is neither a select option nor `knownPackage` (the saved package the server knows).
 */
export function appPackageErrors(
  value: AppPackageValue,
  knownPackage?: string | null,
): Partial<Record<AppPackageField, string>> {
  const errors: Partial<Record<AppPackageField, string>> = {};
  let needsAppStoreId = false;
  if (value.choice === OTHER_APP_PACKAGE) {
    const pkg = normalizePackage(value.other);
    if (!pkg) errors.appScheme = APP_PACKAGE_REQUIRED_ERROR;
    else if (!PACKAGE_PATTERN.test(pkg)) errors.appScheme = APP_PACKAGE_FORMAT_ERROR;
    else needsAppStoreId = !OPTION_PACKAGES.includes(pkg) && pkg !== normalizePackage(knownPackage);
  }
  const id = value.appStoreId.trim();
  if (id && !APP_STORE_ID_PATTERN.test(id)) errors.appStoreId = APP_STORE_ID_FORMAT_ERROR;
  else if (!id && needsAppStoreId) errors.appStoreId = APP_STORE_ID_REQUIRED_ERROR;
  return errors;
}

interface AppPackageFieldsProps {
  /** Prefix for element ids (unique per form). */
  idPrefix: string;
  value: AppPackageValue;
  onChange: (value: AppPackageValue) => void;
  errors?: Partial<Record<AppPackageField, string>>;
  /** A saved known package that is not a select option (shown as itself). */
  extraKnownPackage?: string | null;
}

/** App package select (Athlyn / Amplify+ / Other…) + the App Store ID — create wizard and edit dialog. */
export function AppPackageFields({ idPrefix, value, onChange, errors = {}, extraKnownPackage }: AppPackageFieldsProps) {
  const packageId = `${idPrefix}-app-package`;
  const otherId = `${idPrefix}-app-package-other`;
  const storeId = `${idPrefix}-app-store-id`;
  const extra = normalizePackage(extraKnownPackage);
  const showExtra = !!extra && !OPTION_PACKAGES.includes(extra);
  const isOther = value.choice === OTHER_APP_PACKAGE;
  const storeIdRequired = appPackageErrors({ ...value, appStoreId: "" }, extraKnownPackage).appStoreId !== undefined;

  return (
    <div className="space-y-4 rounded-lg border p-4" data-testid={`${idPrefix}-app-fields`}>
      <div className="space-y-2">
        <Label htmlFor={packageId}>App package</Label>
        <Select
          value={value.choice}
          onValueChange={(choice) =>
            // A known app brings its own App Store ID, and the server prefers a row's ID over it — so
            // an ID left over from an earlier choice would send iPhones to the wrong store. Clear it
            // on any known-app choice; "Other…" keeps what was typed.
            onChange({ ...value, choice, appStoreId: choice === OTHER_APP_PACKAGE ? value.appStoreId : "" })
          }
        >
          <SelectTrigger
            id={packageId}
            aria-describedby={`${packageId}-help`}
            aria-invalid={!isOther && !!errors.appScheme}
          >
            <SelectValue placeholder="No app set" />
          </SelectTrigger>
          <SelectContent>
            {APP_PACKAGE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
            {showExtra && <SelectItem value={extra}>{extra}</SelectItem>}
            <SelectItem value={OTHER_APP_PACKAGE}>Other…</SelectItem>
          </SelectContent>
        </Select>
        <p id={`${packageId}-help`} className="text-xs text-muted-foreground">
          {APP_PACKAGE_HELP}
        </p>
      </div>

      {isOther && (
        <div className="space-y-2">
          <Label htmlFor={otherId}>Package name</Label>
          <Input
            id={otherId}
            value={value.other}
            onChange={(e) => onChange({ ...value, other: e.target.value })}
            placeholder="com.company.app"
            autoCapitalize="none"
            spellCheck={false}
            aria-invalid={!!errors.appScheme}
            aria-describedby={errors.appScheme ? `${otherId}-error` : undefined}
          />
          {errors.appScheme && (
            <p id={`${otherId}-error`} className="text-sm text-destructive">
              {errors.appScheme}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={storeId}>{storeIdRequired ? "App Store ID *" : "App Store ID (optional)"}</Label>
        <Input
          id={storeId}
          value={value.appStoreId}
          onChange={(e) => onChange({ ...value, appStoreId: e.target.value })}
          placeholder="6758803089"
          inputMode="numeric"
          aria-invalid={!!errors.appStoreId}
          aria-describedby={`${storeId}-help${errors.appStoreId ? ` ${storeId}-error` : ""}`}
        />
        {errors.appStoreId && (
          <p id={`${storeId}-error`} className="text-sm text-destructive">
            {errors.appStoreId}
          </p>
        )}
        <p id={`${storeId}-help`} className="text-xs text-muted-foreground">
          {APP_STORE_ID_HELP}
        </p>
      </div>
    </div>
  );
}

export default AppPackageFields;
